
// Rate limiting utilities for Close API
import { API_RATE_LIMIT_SETTINGS } from '@/services/closeApi/constants';

// Global object to track API rate limiting
// This is intentionally on the window object so it persists across module reloads during development
if (typeof window !== 'undefined') {
  if (!window.API_RATE_LIMIT) {
    window.API_RATE_LIMIT = {
      lastRequest: 0,
      inRateLimitCooldown: false,
      cooldownEndTime: 0,
      consecutiveErrors: 0,
      lastSuccessfulRequest: {},
      networkErrorCount: 0,    // Track network errors
      lastNetworkError: null,  // Track last network error message
      lastNetworkErrorTime: 0  // Track when the last network error occurred
    };
  }
}

// Check if we're in a rate limit cooldown
export const isInRateLimitCooldown = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  if (window.API_RATE_LIMIT?.inRateLimitCooldown) {
    const now = Date.now();
    if (now >= window.API_RATE_LIMIT.cooldownEndTime) {
      // Cooldown period has elapsed, reset
      window.API_RATE_LIMIT.inRateLimitCooldown = false;
      window.API_RATE_LIMIT.consecutiveErrors = 0;
      return false;
    }
    return true;
  }
  return false;
};

// Get remaining cooldown time in milliseconds
export const getRemainingCooldownTime = (): number => {
  if (typeof window === 'undefined') return 0;
  
  if (window.API_RATE_LIMIT?.inRateLimitCooldown) {
    const now = Date.now();
    const remaining = Math.max(0, window.API_RATE_LIMIT.cooldownEndTime - now);
    return remaining;
  }
  return 0;
};

// Format a time in milliseconds to a human-readable string
export const formatTimeRemaining = (milliseconds: number): string => {
  if (milliseconds < 1000) {
    return `${milliseconds}ms`;
  }
  
  const seconds = Math.floor(milliseconds / 1000);
  if (seconds < 60) {
    return `${seconds} Sekunde${seconds !== 1 ? 'n' : ''}`;
  }
  
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  
  if (minutes < 60) {
    if (remainingSeconds === 0) {
      return `${minutes} Minute${minutes !== 1 ? 'n' : ''}`;
    }
    return `${minutes} Min. ${remainingSeconds} Sek.`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  return `${hours} Std. ${remainingMinutes} Min.`;
};

// Check if it's too soon to make another request to a specific endpoint
export const isTooSoonForEndpoint = (endpointKey: string): boolean => {
  if (typeof window === 'undefined') return false;
  
  const now = Date.now();
  const lastSuccessfulRequest = window.API_RATE_LIMIT?.lastSuccessfulRequest?.[endpointKey] || 0;
  
  return (now - lastSuccessfulRequest) < API_RATE_LIMIT_SETTINGS.MIN_ENDPOINT_REQUEST_INTERVAL;
};

// Get diagnostic information about the network state
export const getNetworkDiagnostics = (): {
  isOnline: boolean;
  networkErrorCount: number;
  lastNetworkError: string | null;
  lastNetworkErrorTime: number;
} => {
  if (typeof window === 'undefined') {
    return {
      isOnline: true,
      networkErrorCount: 0,
      lastNetworkError: null,
      lastNetworkErrorTime: 0
    };
  }
  
  return {
    isOnline: navigator.onLine,
    networkErrorCount: window.API_RATE_LIMIT?.networkErrorCount || 0,
    lastNetworkError: window.API_RATE_LIMIT?.lastNetworkError || null,
    lastNetworkErrorTime: window.API_RATE_LIMIT?.lastNetworkErrorTime || 0
  };
};

// Track a network error
export const trackNetworkError = (errorMessage: string): void => {
  if (typeof window === 'undefined') return;
  
  window.API_RATE_LIMIT.networkErrorCount = (window.API_RATE_LIMIT.networkErrorCount || 0) + 1;
  window.API_RATE_LIMIT.lastNetworkError = errorMessage;
  window.API_RATE_LIMIT.lastNetworkErrorTime = Date.now();
  
  console.warn(`Close API network error #${window.API_RATE_LIMIT.networkErrorCount}: ${errorMessage}`);
};

// Reset network error tracking
export const resetNetworkErrors = (): void => {
  if (typeof window === 'undefined') return;
  
  window.API_RATE_LIMIT.networkErrorCount = 0;
  window.API_RATE_LIMIT.lastNetworkError = null;
  window.API_RATE_LIMIT.lastNetworkErrorTime = 0;
};

// Enforce time between requests to avoid hitting rate limits
export const enforceRequestInterval = async (endpointKey?: string): Promise<void> => {
  if (typeof window === 'undefined') return;
  
  const now = Date.now();
  
  // Check for endpoint-specific rate limiting
  if (endpointKey && isTooSoonForEndpoint(endpointKey)) {
    throw new Error(`Zu viele Anfragen für ${endpointKey}. Bitte warten Sie einige Minuten.`);
  }

  // Check for global rate limiting
  if (isInRateLimitCooldown()) {
    const cooldownTimeRemaining = getRemainingCooldownTime();
    const formattedTime = formatTimeRemaining(cooldownTimeRemaining);
    throw new Error(`API-Ratenbegrenzung erreicht. Bitte warten Sie ${formattedTime}.`);
  }
  
  // Enforce minimum interval between requests
  const timeSinceLastRequest = now - (window.API_RATE_LIMIT?.lastRequest || 0);
  if (timeSinceLastRequest < API_RATE_LIMIT_SETTINGS.DEFAULT_REQUEST_INTERVAL) {
    const delayNeeded = API_RATE_LIMIT_SETTINGS.DEFAULT_REQUEST_INTERVAL - timeSinceLastRequest;
    await new Promise(resolve => setTimeout(resolve, delayNeeded));
  }
  
  // Update last request time
  window.API_RATE_LIMIT.lastRequest = Date.now();
};

// Track successful request to an endpoint
export const trackSuccessfulRequest = (endpointKey: string): void => {
  if (typeof window === 'undefined') return;
  
  // Reset consecutive errors and network errors on successful request
  window.API_RATE_LIMIT.consecutiveErrors = 0;
  resetNetworkErrors();
  
  // Track the last successful request time for this endpoint
  if (!window.API_RATE_LIMIT.lastSuccessfulRequest) {
    window.API_RATE_LIMIT.lastSuccessfulRequest = {};
  }
  
  window.API_RATE_LIMIT.lastSuccessfulRequest[endpointKey] = Date.now();
};

// Handle rate limit error
export const handleRateLimitError = (error: any, headers?: Headers): boolean => {
  if (typeof window === 'undefined') return false;
  
  // Increment consecutive errors
  window.API_RATE_LIMIT.consecutiveErrors = (window.API_RATE_LIMIT.consecutiveErrors || 0) + 1;
  
  // Check if this is a network error related to CORS or connectivity
  const errorMessage = error?.message || '';
  if (
    errorMessage.includes('Network Error') || 
    errorMessage.includes('NetworkError') ||
    errorMessage.includes('Netzwerkfehler') ||
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('CORS')
  ) {
    trackNetworkError(errorMessage);
    
    // Apply a shorter cooldown for network errors
    const cooldownTime = Math.min(30000, API_RATE_LIMIT_SETTINGS.COOLDOWN_BASE * window.API_RATE_LIMIT.networkErrorCount);
    window.API_RATE_LIMIT.inRateLimitCooldown = true;
    window.API_RATE_LIMIT.cooldownEndTime = Date.now() + cooldownTime;
    
    console.log(`Network error detected, cooling down for ${formatTimeRemaining(cooldownTime)}`);
    return true;
  }
  
  // Check if this is likely a rate limit error
  let isRateLimitError = false;
  
  if (headers) {
    // Check headers for rate limit information
    const remaining = headers.get(API_RATE_LIMIT_SETTINGS.RATE_LIMIT_REMAINING_HEADER);
    const resetTime = headers.get(API_RATE_LIMIT_SETTINGS.RATE_LIMIT_RESET_HEADER);
    
    if (remaining === '0' || (error?.status === 429)) {
      isRateLimitError = true;
      
      // Calculate cooldown time based on reset header or default
      let cooldownTime = API_RATE_LIMIT_SETTINGS.COOLDOWN_BASE;
      if (resetTime) {
        const resetTimestamp = new Date(resetTime).getTime();
        cooldownTime = Math.max(cooldownTime, resetTimestamp - Date.now());
      }
      
      window.API_RATE_LIMIT.inRateLimitCooldown = true;
      window.API_RATE_LIMIT.cooldownEndTime = Date.now() + cooldownTime;
      
      console.log(`Rate limit hit, cooling down for ${formatTimeRemaining(cooldownTime)}`);
      return true;
    }
  }
  
  // Check error message or status code
  if (error) {
    const statusCode = error.status || 0;
    
    if (statusCode === 429 || 
        errorMessage.toLowerCase().includes('rate limit') || 
        errorMessage.toLowerCase().includes('too many requests')) {
      isRateLimitError = true;
    }
  }
  
  // Apply cooldown if rate limited or after multiple consecutive errors
  if (isRateLimitError || window.API_RATE_LIMIT.consecutiveErrors >= 5) {
    // Calculate escalating cooldown based on consecutive errors
    const cooldownFactor = isRateLimitError ? 2 : (1 + (window.API_RATE_LIMIT.consecutiveErrors * 0.2));
    const cooldownTime = API_RATE_LIMIT_SETTINGS.COOLDOWN_BASE * cooldownFactor;
    
    window.API_RATE_LIMIT.inRateLimitCooldown = true;
    window.API_RATE_LIMIT.cooldownEndTime = Date.now() + cooldownTime;
    
    console.log(`Cooling down for ${formatTimeRemaining(cooldownTime)} after ${window.API_RATE_LIMIT.consecutiveErrors} consecutive errors`);
    return true;
  }
  
  return false;
};

// Update rate limit information from response headers
export const updateRateLimitFromHeaders = (headers: Headers): void => {
  if (typeof window === 'undefined' || !headers) return;
  
  try {
    const remaining = headers.get(API_RATE_LIMIT_SETTINGS.RATE_LIMIT_REMAINING_HEADER);
    if (remaining !== null) {
      const remainingCount = parseInt(remaining, 10);
      console.log(`API rate limit remaining: ${remainingCount}`);
      
      // If very few calls remaining, throttle more
      if (remainingCount <= 5) {
        const resetTimeHeader = headers.get(API_RATE_LIMIT_SETTINGS.RATE_LIMIT_RESET_HEADER);
        if (resetTimeHeader) {
          const resetTime = new Date(resetTimeHeader).getTime();
          const now = Date.now();
          if (resetTime > now) {
            console.log(`Rate limit will reset in ${formatTimeRemaining(resetTime - now)}`);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error updating rate limit from headers:', error);
  }
};
